import { doc, setDoc } from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { ErrorText, LoadingView, PrimaryButton, SBCard, SecondaryButton } from "@/components/ui";
import { useAppEnvironmentReport } from "@/features/ai/AppEnvironmentScreen";
import type { SBotAppEnvironmentSurfaceReport } from "@/features/ai/services/appEnvironmentContext";
import { db } from "@/firebase/app";
import { useDoc } from "@/hooks/firestoreHooks";
import {
  competitionInstanceFirestoreData,
  parseCompetitionInstance,
  parseCompetitionTemplate,
  templateActiveFields,
  type LeagueCompetitionInstanceStatus,
} from "@/models/competition";
import { scopedMapsByDerivingAthleteUniversalFields } from "@/models/athleteUniversalFields";
import { eventDisplay, locationText } from "@/models/eventDisplay";
import { activeLeagueManagementMembership, activeMembership, canShowGPSAssignmentsForMembership } from "@/models/user";
import { humanActionAuditActor, recordActionAudit } from "@/services/actionAudit";
import { DynamicValuesEditor, type ScopedValueMaps } from "./DynamicValuesEditor";
import "@/features/home/home.css";

// Port of CompetitionView: event header + status transitions + dynamic values
// editing for league managers, plus links into courses and GPS tools.

export function CompetitionDetail() {
  const { instanceId } = useParams<{ instanceId: string }>();
  const { currentUser, isSBotLabsAdmin } = useAuth();

  const instance = useDoc(instanceId ? doc(db, "leagueCompetitionInstances", instanceId) : null, parseCompetitionInstance);
  const template = useDoc(
    instance.data ? doc(db, "leagueCompetitionTemplates", instance.data.templateID) : null,
    parseCompetitionTemplate,
  );

  const [maps, setMaps] = useState<ScopedValueMaps>({ publicValues: {}, organizationValues: {}, leagueValues: {} });
  const [isDirty, setIsDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [loadedRevision, setLoadedRevision] = useState<string | null>(null);

  useEffect(() => {
    if (!instance.data) return;
    const revision = `${instance.data.id}:${instance.data.updatedAt?.getTime() ?? 0}`;
    if (revision !== loadedRevision && !isDirty) {
      setMaps({
        publicValues: instance.data.publicValues,
        organizationValues: instance.data.organizationValues,
        leagueValues: instance.data.leagueValues,
      });
      setLoadedRevision(revision);
    }
  }, [instance.data, loadedRevision, isDirty]);

  const display = useMemo(
    () => (instance.data ? eventDisplay(instance.data, template.data ?? undefined) : null),
    [instance.data, template.data],
  );

  const canManage = isSBotLabsAdmin || (currentUser ? activeLeagueManagementMembership(currentUser) !== undefined : false);
  const canManageGPS = isSBotLabsAdmin || (currentUser ? canShowGPSAssignmentsForMembership(activeMembership(currentUser)) : false);
  const fields = template.data ? templateActiveFields(template.data) : [];
  const environmentReport = useMemo<SBotAppEnvironmentSurfaceReport>(() => {
    const title = display?.eventName ?? "Competition";
    return {
      identity: "competition-detail",
      owningTab: "home",
      breadcrumbs: ["Home", "Competitions", title],
      destinationType: "competition",
      entityDisplayName: display?.eventName,
      surfaceLevel: "leaf",
      surfaceDepth: 2,
      surfacePresentation: "route",
      screen: {
        title,
        state: instance.error
          ? "error"
          : instance.loading || template.loading
            ? "loading"
            : !instance.data || !display
              ? "unavailable"
              : canManage
                ? "form; unsaved values undisclosed"
                : "visible",
        contextCompleteness: "basic",
        sections: [{
          id: "competition-current-view",
          title: "Current View",
          controls: [{
            role: "status",
            label: display?.eventName ?? "Competition",
            value: instance.data?.status,
          }],
        }],
      },
    };
  }, [canManage, display, instance.data, instance.error, instance.loading, template.loading]);
  useAppEnvironmentReport(environmentReport);

  const save = async (statusOverride?: LeagueCompetitionInstanceStatus) => {
    if (!instance.data || !currentUser) return;
    setIsSaving(true);
    setError(null);
    setMessage(null);
    try {
      const timestamp = new Date();
      const derivedMaps = scopedMapsByDerivingAthleteUniversalFields(maps, fields, [], timestamp);
      const next = {
        ...instance.data,
        status: statusOverride ?? instance.data.status,
        publicValues: derivedMaps.publicValues,
        organizationValues: derivedMaps.organizationValues,
        leagueValues: derivedMaps.leagueValues,
        updatedAt: timestamp,
        updatedByUserID: currentUser.id,
      };
      await setDoc(doc(db, "leagueCompetitionInstances", next.id), competitionInstanceFirestoreData(next));
      void recordActionAudit({
        before: competitionInstanceFirestoreData(instance.data),
        after: competitionInstanceFirestoreData(next),
        context: {
          leagueID: next.leagueID,
          targetCollection: "leagueCompetitionInstances",
          targetDocumentID: next.id,
          targetDisplayName: next.name,
          actionType: statusOverride === "archived" ? "archive" : "update",
          actor: humanActionAuditActor(currentUser),
          fieldLabels: actionAuditFieldLabels(fields),
        },
      });
      setIsDirty(false);
      setMessage(statusOverride ? `Competition ${statusOverride}.` : "Saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed.");
    } finally {
      setIsSaving(false);
    }
  };

  if (instance.loading && !instance.data) return <LoadingView />;
  if (instance.error) return <ErrorText>{instance.error}</ErrorText>;
  if (!instance.data || !display) return <ErrorText>Competition not found.</ErrorText>;

  const location = locationText(display);
  const status = instance.data.status;

  return (
    <div className="list-screen">
      <div className="list-screen-head">
        <div>
          <h1>{display.eventName}</h1>
          <p className="home-hint">
            {display.startsAt
              ? display.startsAt.toLocaleString(undefined, { dateStyle: "full", timeStyle: "short" })
              : "Unscheduled"}
            {location ? ` · ${location}` : ""}
            {display.eventCode ? ` · Code ${display.eventCode}` : ""}
          </p>
        </div>
        <div className="event-row-badges">
          {display.isGPSRace && <span className="badge badge-gps">GPS</span>}
          <span className={`badge badge-${status}`}>{status}</span>
        </div>
      </div>

      {(display.isGPSRace || display.courseKMLs.length > 0) && (
        <SBCard className="home-card">
          <div className="home-stats gps-menu-actions">
            {display.courseKMLs.length > 0 && (
              <Link className="home-stat" to={`/competitions/${instance.data.id}/courses`}>
                <strong>Courses</strong>
                <span>{display.courseKMLs.length} KML</span>
              </Link>
            )}
            {display.isGPSRace && (
              <>
                <Link className="home-stat" to={`/competitions/${instance.data.id}/gps/map`}>
                  <strong>Live Map</strong>
                  <span>Realtime</span>
                </Link>
                {canManageGPS && (
                  <Link className="home-stat" to={`/competitions/${instance.data.id}/gps/assignments`}>
                    <strong>GPS IDs</strong>
                    <span>Assignments</span>
                  </Link>
                )}
                {canManageGPS && (
                  <Link className="home-stat" to={`/competitions/${instance.data.id}/gps/share`}>
                    <strong>Share</strong>
                    <span>Public link</span>
                  </Link>
                )}
                <Link className="home-stat" to={`/competitions/${instance.data.id}/gps/history`}>
                  <strong>History</strong>
                  <span>Playback</span>
                </Link>
              </>
            )}
          </div>
        </SBCard>
      )}

      {canManage && (
        <SBCard className="home-card">
          <div className="home-card-head">
            <h3>Competition data</h3>
            <span className="badge">{instance.data.templateName}</span>
          </div>
          <DynamicValuesEditor
            fields={fields}
            maps={maps}
            onChange={(next) => {
              setMaps(next);
              setIsDirty(true);
            }}
          />
          <PrimaryButton title={isSaving ? "Saving…" : "Save changes"} onClick={() => void save()} disabled={isSaving || !isDirty} />
          <div style={{ display: "flex", gap: 10 }}>
            {status === "draft" && <SecondaryButton title="Publish" onClick={() => void save("published")} disabled={isSaving} />}
            {status === "published" && <SecondaryButton title="Back to draft" onClick={() => void save("draft")} disabled={isSaving} />}
            {status !== "archived" ? (
              <SecondaryButton title="Archive" destructive onClick={() => void save("archived")} disabled={isSaving} />
            ) : (
              <SecondaryButton title="Restore to draft" onClick={() => void save("draft")} disabled={isSaving} />
            )}
          </div>
          <ErrorText>{error}</ErrorText>
          {message && <p className="home-hint">{message}</p>}
        </SBCard>
      )}
    </div>
  );
}

function actionAuditFieldLabels(fields: ReturnType<typeof templateActiveFields>): Record<string, string> {
  return Object.fromEntries(
    fields.flatMap((field) =>
      ["publicValues", "organizationValues", "leagueValues"].map((prefix) => [`${prefix}.${field.id}`, field.label]),
    ),
  );
}
